import axe from "axe-core";
// webpack asset/source rule returns axe.min.js as a raw IIFE string for iframe injection.
import axeMinSource from "axe-core/axe.min.js";

import type { A11yScanOptions, A11yScanResult } from "./types";

type FrameWindow = Window & { axe?: typeof axe };

const AXE_OPTIONS: axe.RunOptions = {
  runOnly: {
    // WCAG A/AA + best-practice (adds heading-order, empty-heading, p-as-heading).
    type: "tag",
    values: ["wcag2a", "wcag2aa", "wcag21a", "wcag21aa", "best-practice"]
  },
  rules: {
    bypass: { enabled: false },
    "skip-link": { enabled: false },
    "document-title": { enabled: false },
    "html-has-lang": { enabled: false },
    "html-lang-valid": { enabled: false },
    "html-xml-lang-mismatch": { enabled: false },
    region: { enabled: false },
    "page-has-heading-one": { enabled: false },
    "landmark-one-main": { enabled: false },
    "landmark-main-is-top-level": { enabled: false },
    "landmark-banner-is-top-level": { enabled: false },
    "landmark-contentinfo-is-top-level": { enabled: false },
    "landmark-complementary-is-top-level": { enabled: false },
    "landmark-no-duplicate-banner": { enabled: false },
    "landmark-no-duplicate-contentinfo": { enabled: false },
    "landmark-no-duplicate-main": { enabled: false },
    "landmark-unique": { enabled: false }
  },
  resultTypes: ["violations", "passes", "incomplete"],
  iframes: true,
  reporter: "v2"
};

// iframes:false prevents injected axe from timing out on cross-origin iframe blocks.
const FRAME_AXE_OPTIONS: axe.RunOptions = { ...AXE_OPTIONS, iframes: false };

// axe passes img[alt=""] as intentionally decorative. In a content editor images with
// empty alt are likely missing a description, so surface them as "Needs Review".
export function findEmptyAltImages(doc: Document): axe.Result | null {
  const imgs = Array.from(
    doc.querySelectorAll<HTMLImageElement>('img[alt=""]')
  );
  if (imgs.length === 0) return null;

  return {
    id: "image-alt",
    impact: "serious" as axe.ImpactValue,
    tags: ["wcag2a", "wcag21a", "wcag111"],
    description:
      "Ensure <img> elements have alternate text or a role of none or presentation",
    help: 'Images with alt="" are marked decorative - verify this is intentional',
    helpUrl: "https://dequeuniversity.com/rules/axe/4.10/image-alt",
    nodes: imgs.map((img) => {
      const src = img.getAttribute("src") ?? "";
      const filename = src.split("/").pop()?.split("?")[0];
      const sel = img.id
        ? `#${img.id}`
        : filename
        ? `img[src$="${filename}"]`
        : 'img[alt=""]';
      return {
        any: [
          {
            id: "image-alt",
            data: null,
            relatedNodes: [],
            impact: "serious",
            message:
              'Element has alt="" - confirm it is decorative or add a descriptive alt attribute'
          }
        ],
        all: [],
        none: [],
        impact: "serious" as axe.ImpactValue,
        html: img.outerHTML,
        target: [sel],
        failureSummary:
          'Verify this image is decorative, or replace alt="" with a meaningful description'
      };
    })
  };
}

// True only when the leaf element itself is a dnd-kit drag handle, not just an ancestor.
function isDndKitTarget(target: Array<string | string[]>): boolean {
  return target.some((part) => {
    const s = Array.isArray(part) ? part.join(" ") : part;
    const leaf = s.split(">").pop()?.trim() ?? s;
    return (
      leaf.includes('aria-roledescription="draggable"') ||
      leaf.includes("aria-roledescription='draggable'")
    );
  });
}

// Removes nodes whose selectors point to Puck's drag-and-drop chrome (absent on published pages).
export function filterEditorChrome(results: axe.Result[]): axe.Result[] {
  return results
    .map((result) => ({
      ...result,
      nodes: result.nodes.filter(
        (node) => !isDndKitTarget(node.target as Array<string | string[]>)
      )
    }))
    .filter((result) => result.nodes.length > 0);
}

// Returns the axe instance on the iframe window, injecting axe.min.js on first call and caching it.
export function getFrameAxe(frame: HTMLIFrameElement): typeof axe {
  const frameWin = frame.contentWindow as FrameWindow | null;
  if (!frameWin) throw new Error("iframe contentWindow is not accessible");

  if (!frameWin.axe) {
    if (!axeMinSource)
      throw new Error(
        "axeMinSource is empty - verify the webpack asset/source rule"
      );
    const script = frame.contentDocument!.createElement("script");
    script.textContent = axeMinSource;
    frame.contentDocument!.head.appendChild(script);
    script.remove();
  }

  if (!frameWin.axe) {
    throw new Error(
      "axe did not register - check for CSP violations blocking inline scripts"
    );
  }

  return frameWin.axe;
}

/**
 * Run a WCAG 2.1 AA scan against the given target.
 *
 * When `options.frame` is supplied and points at a same-origin iframe, axe is
 * injected into the frame's contentWindow and run there - this is how the
 * LayoutEditor scans Puck's srcdoc preview and how the CKEditor 4
 * `wysiwygarea` content is scanned.
 */
export async function runA11yScan(
  target: Element,
  options: A11yScanOptions = {}
): Promise<A11yScanResult> {
  const { frame } = options;
  let results: axe.AxeResults;

  if (frame?.contentDocument && frame?.contentWindow) {
    const frameAxe = getFrameAxe(frame);
    results = await frameAxe.run(frame.contentDocument, FRAME_AXE_OPTIONS);
  } else {
    results = await axe.run(target, AXE_OPTIONS);
  }

  const violations = filterEditorChrome(results.violations);
  const emptyAlt = frame?.contentDocument
    ? findEmptyAltImages(frame.contentDocument)
    : null;
  const incomplete = filterEditorChrome([
    ...results.incomplete,
    ...(emptyAlt ? [emptyAlt] : [])
  ]);
  const failingNodes = violations.reduce((n, v) => n + v.nodes.length, 0);
  const passingNodes = results.passes.reduce((n, p) => n + p.nodes.length, 0);
  const total = passingNodes + failingNodes;

  return {
    violations,
    incomplete,
    passCount: passingNodes,
    score: total > 0 ? Math.round((passingNodes / total) * 100) : 100
  };
}
